// Lightweight pre-registration opt-in. Not registration — just "send me the details".
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { createLead } from "@/lib/api";
import { track } from "@/lib/tracker";
import { getStoredReferrer } from "@/lib/referrer";

export default function LeadCapture() {
  const [form, setForm] = useState({ first_name: "", email: "", phone: "", referred_by: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [touched, setTouched] = useState(false);
  const [fromLink, setFromLink] = useState(false);

  useEffect(() => {
    const ref = getStoredReferrer();
    if (ref) {
      setForm((s) => (s.referred_by ? s : { ...s, referred_by: ref }));
      setFromLink(true);
    }
  }, []);

  const update = (k, v) => {
    if (!touched) { setTouched(true); track("form_start", { form: "lead_capture" }); }
    setForm((s) => ({ ...s, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email) {
      toast.error("First name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      await createLead(form);
      track("form_complete", { form: "lead_capture" });
      setDone(true);
      toast.success("Details on the way to your inbox.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="lead-capture-section" className="relative py-20 lg:py-24 border-t border-[var(--line)] bg-bone">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-4">Not Ready to Register Yet?</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-[52px] leading-[1.02] tracking-[-0.035em] text-ink">
              Want the AI Build Lab details{" "}
              <span className="font-serif-em italic text-brand font-light">sent to you?</span>
            </h2>
            <p className="mt-5 text-[16px] text-ink/70 max-w-xl leading-relaxed">
              Drop your name and email. We&apos;ll send you the full details and a few short notes so you can decide on your own time. No spam, unsubscribe anytime.
            </p>
          </div>

          <div className="lg:col-span-5">
            {done ? (
              <div className="bg-white border border-[var(--line)] rounded-3xl p-8 flex items-start gap-4" data-testid="lead-capture-success">
                <div className="w-10 h-10 rounded-full bg-brand-soft border border-brand/20 grid place-items-center text-brand shrink-0">
                  <CheckCircle2 size={20} />
                </div>
                <div>
                  <div className="font-display text-xl text-ink leading-tight">You&apos;re on the list.</div>
                  <div className="text-sm text-ink/65 mt-1.5 leading-relaxed">
                    Check your inbox &mdash; details are on the way. Whenever you&apos;re ready, your seat is one click away.
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="lead-capture-form" className="bg-white border border-[var(--line)] rounded-3xl p-7 space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <Mail size={16} className="text-brand" />
                  <span className="text-[11px] uppercase tracking-[0.2em] text-ink/65 font-medium font-mono">Send Me the Details</span>
                </div>

                <div>
                  <Label htmlFor="lc_first" className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-medium font-mono">First Name *</Label>
                  <Input
                    id="lc_first"
                    data-testid="lc-first-name"
                    value={form.first_name}
                    onChange={(e) => update("first_name", e.target.value)}
                    className="bg-bone border border-[var(--line)] text-ink h-11 mt-1.5 focus-visible:ring-brand focus-visible:ring-offset-0"
                  />
                </div>
                <div>
                  <Label htmlFor="lc_email" className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-medium font-mono">Email *</Label>
                  <Input
                    id="lc_email"
                    type="email"
                    data-testid="lc-email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    className="bg-bone border border-[var(--line)] text-ink h-11 mt-1.5 focus-visible:ring-brand focus-visible:ring-offset-0"
                  />
                </div>
                <div>
                  <Label htmlFor="lc_phone" className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-medium font-mono">Phone (optional)</Label>
                  <Input
                    id="lc_phone"
                    type="tel"
                    data-testid="lc-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    className="bg-bone border border-[var(--line)] text-ink h-11 mt-1.5 focus-visible:ring-brand focus-visible:ring-offset-0"
                  />
                </div>
                <div>
                  <Label htmlFor="lc_referred_by" className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-medium font-mono">
                    Referred By {fromLink ? <span className="text-brand normal-case tracking-normal font-medium">· auto-filled from your link</span> : "(optional)"}
                  </Label>
                  <Input
                    id="lc_referred_by"
                    data-testid="lc-referred-by"
                    value={form.referred_by}
                    onChange={(e) => update("referred_by", e.target.value)}
                    placeholder="Name of the person who told you about this"
                    className="bg-bone border border-[var(--line)] text-ink h-11 mt-1.5 focus-visible:ring-brand focus-visible:ring-offset-0"
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="lc-submit"
                  data-cta="lead-capture"
                  disabled={submitting}
                  className="w-full h-12 rounded-full btn-ink font-medium text-base mt-2"
                >
                  {submitting ? (
                    <><Loader2 className="animate-spin mr-2" size={16} /> Sending...</>
                  ) : (
                    <>Send Me the Details <ArrowRight className="ml-2" size={16} /></>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
